// projectSlice.ts
import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { IProject, IProjectInitialState } from "./projectModal";
import {
  activeInactiveProject,
  createProject,
  deleteProject,
  getAllActiveProjects,
  getAllProjects,
  getProjectByID,
  getProjectByURL,
  projectImageUpload,
  updateProject,
} from "./services/project.services";

const initialState: IProjectInitialState = {
  loading: false,
  formLoading: false,
  error: "",
  ProjectID: null,
  ProjectList: [],
  ProjectDetail: {
    ID: null,
    Title: "",
    URL: "",
    Tagline: "",
    PropertyTypeID: null,
    PropertyStatusID: null,
    AreaID: null,
    ShortDescription: "",
    LongDescription: "",
    SquareFoot: "",
    PriceRange: "",
    SequenceNo: null,
    IsDisplayInHome: false,
    MetaTitle: "",
    MetaKeywords: "",
    MetaDescription: "",
    MainImage: "",
    BannerImage: "",
    IsActive: 1,
  },
  FilterProject: {
    Title: "",
    PropertyTypeName: "",
    PropertyStatusName: "",
    AreaName: "",
  },
};

const projectSlice = createSlice({
  name: "project",
  initialState,
  reducers: {
    clearProjectDetails: (state) => {
      state.ProjectDetail = initialState.ProjectDetail;
    },
    setProjectID: (state, action) => {
      state.ProjectID = action.payload;
    },
    clearProjectID: (state) => {
      state.ProjectID = initialState.ProjectID;
    },
    setFilterProject: (state, action) => {
      state.FilterProject = action.payload;
    },
    clearFilterProject: (state) => {
      state.FilterProject = initialState.FilterProject;
    },
  },
  extraReducers: (builder) => {
    // GET ALL
    builder.addCase(getAllProjects.pending, (state) => {
      state.loading = true;
      state.error = "";
    });
    builder.addCase(
      getAllProjects.fulfilled,
      (state, action: PayloadAction<IProject[]>) => {
        state.loading = false;
        state.ProjectList = action.payload || [];
      },
    );
    builder.addCase(getAllProjects.rejected, (state, action) => {
      state.loading = false;
      state.error = action.error.message || "Something went wrong";
      state.ProjectList = [];
    });

    // CREATE
    builder.addCase(createProject.pending, (state) => {
      state.formLoading = true;
      state.error = "";
    });
    builder.addCase(createProject.fulfilled, (state) => {
      state.formLoading = false;
    });
    builder.addCase(createProject.rejected, (state, action) => {
      state.formLoading = false;
      state.error = action.error.message || "Something went wrong";
    });

    // UPDATE
    builder.addCase(updateProject.pending, (state) => {
      state.formLoading = true;
      state.error = "";
    });
    builder.addCase(updateProject.fulfilled, (state) => {
      state.formLoading = false;
    });
    builder.addCase(updateProject.rejected, (state, action) => {
      state.formLoading = false;
      state.error = action.error.message || "Something went wrong";
    });

    // DELETE
    builder.addCase(deleteProject.pending, (state) => {
      state.loading = true;
      state.error = "";
    });
    builder.addCase(deleteProject.fulfilled, (state) => {
      state.loading = false;
    });
    builder.addCase(deleteProject.rejected, (state, action) => {
      state.loading = false;
      state.error = action.error.message || "Something went wrong";
    });

    // GET BY ID
    builder.addCase(getProjectByID.pending, (state) => {
      state.formLoading = true;
      state.error = "";
    });
    builder.addCase(
      getProjectByID.fulfilled,
      (state, action: PayloadAction<IProject>) => {
        state.formLoading = false;
        state.ProjectDetail = action.payload;
      },
    );
    builder.addCase(getProjectByID.rejected, (state, action) => {
      state.formLoading = false;
      state.ProjectDetail = initialState.ProjectDetail;
      state.error = action.error.message || "Something went wrong";
    });

    // GET BY URL
    builder.addCase(getProjectByURL.pending, (state) => {
      state.formLoading = true;
      state.error = "";
    });
    builder.addCase(
      getProjectByURL.fulfilled,
      (state, action: PayloadAction<IProject>) => {
        state.formLoading = false;
        state.ProjectDetail = action.payload;
      },
    );
    builder.addCase(getProjectByURL.rejected, (state, action) => {
      state.formLoading = false;
      state.ProjectDetail = initialState.ProjectDetail;
      state.error = action.error.message || "Something went wrong";
    });

    // GET ALL ACTIVE
    builder.addCase(getAllActiveProjects.pending, (state) => {
      state.loading = true;
      state.error = "";
    });
    builder.addCase(
      getAllActiveProjects.fulfilled,
      (state, action: PayloadAction<IProject[]>) => {
        state.loading = false;
        state.ProjectList = action.payload || [];
      },
    );
    builder.addCase(getAllActiveProjects.rejected, (state, action) => {
      state.loading = false;
      state.error = action.error.message || "Something went wrong";
      state.ProjectList = [];
    });

    // ACTIVE / INACTIVE
    builder.addCase(activeInactiveProject.pending, (state) => {
      state.loading = true;
      state.error = "";
    });
    builder.addCase(activeInactiveProject.fulfilled, (state) => {
      state.loading = false;
    });
    builder.addCase(activeInactiveProject.rejected, (state, action) => {
      state.loading = false;
      state.error = action.error.message || "Something went wrong";
    });

    // IMAGE UPLOAD
    builder.addCase(projectImageUpload.pending, (state) => {
      state.formLoading = true;
      state.error = "";
    });
    builder.addCase(projectImageUpload.fulfilled, (state) => {
      state.formLoading = false;
    });
    builder.addCase(projectImageUpload.rejected, (state, action) => {
      state.formLoading = false;
      state.error = action.error.message || "Something went wrong";
    });
  },
});

export const {
  clearProjectDetails,
  setProjectID,
  clearProjectID,
  setFilterProject,
  clearFilterProject,
} = projectSlice.actions;

export default projectSlice.reducer;
